import forOwn from 'lodash/forOwn';
import { Subject } from 'rxjs';
import { loadScript } from './scripts';
import { attachMarkerAnimation } from '../map/marker-animate';
import { IZoomControlPositioning } from '../map/map.component';
import { getLocale } from './i18n';
import { getGoogleMapsKey } from './environments.const';

const GOOGLE_MAPS_KEY: string = getGoogleMapsKey();
const currentLocale: string = getLocale();
export type Map = google.maps.Map;
const GOOGLE_MAPS_INVALIDATE_INTERVAL: number = 12 * 3600 * 1000;
const timestamp: number = Math.floor(Date.now() / GOOGLE_MAPS_INVALIDATE_INTERVAL) * GOOGLE_MAPS_INVALIDATE_INTERVAL;

let src = `https://maps.googleapis.com/maps/api/js?libraries=geometry,places,marker&t=${timestamp}`;

if (GOOGLE_MAPS_KEY) {
  src = `${src}&key=${GOOGLE_MAPS_KEY}&language=${currentLocale}`;
}

export const gmapsLoadPromise = loadScript(src)
  .then(() => (window as any).google.maps)
  .catch(error => {
    console.error('Google Maps API failed:', error);
    // Catch error to allow dashboard to load, some features will be inaccessible
    return null;
  });

export function createMap(elem, options = {}) {
  return gmapsLoadPromise.then(gmaps => {
    const map: Map = new gmaps.Map(elem, options);
    attachMarkerAnimation(gmaps, map);
    return new Promise(resolve =>
      // projection_changed is used to avoid getting undefined from map.getProjection()
      gmaps.event.addListenerOnce(map, 'projection_changed', () => resolve(map)),
    );
  });
}

export function initMapControls(
  element: HTMLElement,
  map: Map, mapOptions: google.maps.MapOptions,
  zoomControlPosition: IZoomControlPositioning,
  beforeZoomChange$: Subject<number>,
): void {
  if (mapOptions.mapTypeControl != false || mapOptions.fullscreenControl != false) initControlsContainer(element, map);
  if (mapOptions.fullscreenControl != false) initFullscreenContainer(element, map);
  if (mapOptions.mapTypeControl != false) initMapTypeControl(element, map);
  if (mapOptions.zoomControl != false) initZoomControl(element, map, zoomControlPosition, beforeZoomChange$);
}

function initZoomControl(
  el, map: Map,
  zoomControlPosition: IZoomControlPositioning,
  beforeZoomChange$: Subject<number>): void {
  el.querySelector('.zoom-control-in').onclick = () => {
    map.setZoom(map.getZoom() + 1);
  };
  el.querySelector('.zoom-control-out').onclick = () => {
    const zoomLevel: number = map.getZoom() - 1;
    if (beforeZoomChange$) beforeZoomChange$.next(zoomLevel);
    map.setZoom(zoomLevel);
  };
  const zoomControl: any = el.querySelector('.zoom-control');
  forOwn(zoomControlPosition, function (value, key) {
    zoomControl.style[key] = value;
  });

  map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(el.querySelector('.zoom-control'));
}

function initMapTypeControl(el, map: Map): void {
  const mapTypeControlDiv = el.querySelector('.maptype-control');

  if (map.getMapTypeId() === google.maps.MapTypeId.SATELLITE) {
    mapTypeControlDiv.classList.add('maptype-control-is-satellite');
    enableSatelliteDropdown(el, map);
  } else {
    mapTypeControlDiv.classList.add('maptype-control-is-map');
    enableTerrainDropdown(el, map);
  }

  el.querySelector('.maptype-control-map').onclick = () => {
    mapTypeControlDiv.classList.add('maptype-control-is-map');
    mapTypeControlDiv.classList.remove('maptype-control-is-satellite');
    setMapTypeMap(el, map);
    showMapDropdown(el);
  };

  el.querySelector('.maptype-control-satellite').onclick = () => {
    mapTypeControlDiv.classList.remove('maptype-control-is-map');
    mapTypeControlDiv.classList.add('maptype-control-is-satellite');
    setMapTypeSatellite(el, map);
    showSatelliteDropdown(el);
  };

  el.querySelector('input[name=terrain]').addEventListener('change', function () {
    if (this.checked) map.setMapTypeId('terrain');
    else map.setMapTypeId('roadmap');
  });

  el.querySelector('input[name=satellite-labels]').addEventListener('change', function () {
    if (this.checked) map.setMapTypeId('hybrid');
    else map.setMapTypeId('satellite');
  });
}

function setMapTypeSatellite(el, map: Map): void {
  if (el.querySelector('input[name=satellite-labels]').checked) map.setMapTypeId('hybrid');
  else map.setMapTypeId('satellite');
  enableSatelliteDropdown(el, map);
}

function setMapTypeMap(el, map: Map): void {
  if (el.querySelector('input[name=terrain]').checked) map.setMapTypeId('terrain');
  else map.setMapTypeId('roadmap');
  enableTerrainDropdown(el, map);
}

function enableTerrainDropdown(el, map: Map): void {
  const terrainDropDown = el.querySelector('.terrain-maptype');
  const satelliteLabelsDropDown = el.querySelector('.satellite-labels-maptype');
  const maptypeMapContainer = el.querySelector('.maptype-map-container');

  maptypeMapContainer.onmouseover = () => {
    terrainDropDown.classList.remove('hidden');
    satelliteLabelsDropDown.classList.add('hidden');
  };

  maptypeMapContainer.onmouseout = () => terrainDropDown.classList.add('hidden');
  el.querySelector('.maptype-satellite-container').onmouseover = null;
}

function enableSatelliteDropdown(el, map: Map): void {
  const satelliteLabelsDropDown = el.querySelector('.satellite-labels-maptype');
  const maptypeSatelliteContainer = el.querySelector('.maptype-satellite-container');

  maptypeSatelliteContainer.onmouseover = () => {
    showSatelliteDropdown(el);
  };

  maptypeSatelliteContainer.onmouseout = () => satelliteLabelsDropDown.classList.add('hidden');
  el.querySelector('.maptype-map-container').onmouseover = null;
}

function showSatelliteDropdown(el): void {
  const satelliteLabelsDropDown = el.querySelector('.satellite-labels-maptype');
  const terrainDropDown = el.querySelector('.terrain-maptype');
  satelliteLabelsDropDown.classList.remove('hidden');
  terrainDropDown.classList.add('hidden');
}

function showMapDropdown(el): void {
  const satelliteLabelsDropDown = el.querySelector('.satellite-labels-maptype');
  const terrainDropDown = el.querySelector('.terrain-maptype');
  satelliteLabelsDropDown.classList.add('hidden');
  terrainDropDown.classList.remove('hidden');
}

function initControlsContainer(el: HTMLElement, map: Map): void {
  const container: HTMLDivElement = el.querySelector('.controls-container') as HTMLDivElement;
  map.controls[google.maps.ControlPosition.RIGHT_TOP].push(container);
}

function initFullscreenContainer(el, map: Map): void {
  const elementToSendFullscreen = map.getDiv().firstChild;
  const fullscreenControl = el.querySelector('.fullscreen-control');
  fullscreenControl.onclick = () => {
    if (isFullscreen(elementToSendFullscreen)) exitFullscreen();
    else requestFullscreen(elementToSendFullscreen);
  };

  (document as any).onwebkitfullscreenchange = (document as any).onmsfullscreenchange =
    (document as any).onmozfullscreenchange = (document).onfullscreenchange = () => {
      if (isFullscreen(elementToSendFullscreen)) fullscreenControl.classList.add('is-fullscreen');
      else fullscreenControl.classList.remove('is-fullscreen');
    };
}

function isFullscreen(element): boolean {
  return ((document as any).fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement) == element;
}

function requestFullscreen(element): void {
  if (element.requestFullscreen) element.requestFullscreen();
  else if (element.webkitRequestFullScreen) element.webkitRequestFullScreen();
  else if (element.mozRequestFullScreen) element.mozRequestFullScreen();
  else if (element.msRequestFullscreen) element.msRequestFullscreen();
}

function exitFullscreen(): void {
  if ((document as any).exitFullscreen) (document as any).exitFullscreen();
  else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
  else if ((document as any).mozCancelFullScreen) (document as any).mozCancelFullScreen();
  else if ((document as any).msExitFullscreen) (document as any).msExitFullscreen();
}
